import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import PropTypes from "prop-types";

const sizeClasses = {
  xs: "sm:max-w-sm",
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
  full: "sm:max-w-7xl",
  fullscreen: "w-[95vw] h-[95vh] max-w-none max-h-none",
};

export function ConfirmFormModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  icon,
  iconClassName = "bg-blue-50", // Default background class for icon container
  children,
  showHeader = true,
  showFooter = true,
  showCloseButton = true,
  showConfirmButton = true,
  showLeftButton = false,
  confirmButtonText = "OK",
  cancelButtonText = "Cancel",
  leftButtonText = "Left Action",
  confirmButtonVariant = "primary",
  cancelButtonVariant = "outline",
  leftButtonVariant = "secondary",
  confirmButtonSize = "default",
  cancelButtonSize = "default",
  leftButtonSize = "default",
  confirmDisabled = false,
  closeDisabled = false,
  leftButtonDisabled = false,
  onLeftButtonClick,
  size = "md",
  headerClassName,
  headerTitleClassName,
  bodyClassName,
  footerClassName,
  confirmButtonClassName,
  leftButtonClassName,
  headerStyles,
  bodyStyle,
  footerStyles,
  confirmButtonStyle,
  leftButtonStyle,
  titleClassName,
  backDropEnabled = true,
  dialogContentClassname = "",
}) {
  const dialogMaxW = sizeClasses[size] || sizeClasses.md;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !backDropEnabled) onClose?.();
      }}
    >
      <DialogContent
        className={cn(
          "w-full bg-white !rounded-3xl shadow-lg p-0 border border-[#F0F0F0]",
          dialogMaxW,
          size === "fullscreen" && "!rounded-lg",
          dialogContentClassname
        )}
      >
        {showHeader && (
          <DialogHeader
            className={cn(
              "flex flex-row justify-between items-center px-6 py-5",
              headerClassName
            )}
            style={headerStyles}
          >
            <div className="flex flex-1 items-center gap-3">
              {icon && (
                <div
                  className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${iconClassName}`}
                >
                  {icon}
                </div>
              )}
              <DialogTitle
                className={cn(
                  "text-xl font-semibold text-[#262626]",
                  titleClassName
                )}
              >
                {title}
              </DialogTitle>
            </div>
            {!closeDisabled && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-md transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </DialogHeader>
        )}
        <div
          className={cn(
            "px-6 text-base text-[#262626] overflow-y-auto",
            size === "fullscreen" && "max-h-[calc(95vh-120px)]",
            bodyClassName
          )}
          style={bodyStyle}
        >
          {children}
        </div>
        {showFooter && (
          <DialogFooter
            className={`px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-100 ${
              footerClassName || ""
            }`}
            style={footerStyles}
          >
            {/* Left side button */}
            {showLeftButton && (
              <Button
                onClick={onLeftButtonClick}
                variant={leftButtonVariant}
                size={leftButtonSize}
                disabled={leftButtonDisabled}
                className={`mr-auto ${leftButtonClassName || ""}`}
                style={leftButtonStyle}
              >
                {leftButtonText}
              </Button>
            )}

            {/* Right side buttons */}
            {showCloseButton && (
              <Button
                onClick={onClose}
                variant={cancelButtonVariant}
                size={cancelButtonSize}
              >
                {cancelButtonText}
              </Button>
            )}

            {showConfirmButton && (
              <Button
                onClick={onConfirm}
                variant={confirmButtonVariant}
                size={confirmButtonSize}
                disabled={confirmDisabled}
                className={confirmButtonClassName}
                style={confirmButtonStyle}
              >
                {confirmButtonText}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

ConfirmFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  icon: PropTypes.node,
  children: PropTypes.node,
  size: PropTypes.oneOf(["xs","sm","md","lg","xl","full","fullscreen"]),
  confirmButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  showConfirmButton: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
  closeDisabled: PropTypes.bool,
};
